import { getUserManager } from "@/auth/oidcConfig";
import { getConfig } from "@/config/config";

const DEFAULT_TIMEOUT_MS = 15_000;

export class ApiClient {
  async fetch<T>(path: string, init?: RequestInit): Promise<T> {
    const token = await this.acquireToken();
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);
    const { apiUrl } = getConfig();
    const url = apiUrl ? `${apiUrl}${path}` : path;

    try {
      const response = await fetch(url, {
        ...init,
        signal: controller.signal,
        headers: {
          ...init?.headers,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const body = await response.text();
        throw new Error(`${response.status} ${response.statusText}: ${body}`);
      }

      return (await response.json()) as T;
    } finally {
      clearTimeout(timeout);
    }
  }

  private async acquireToken(): Promise<string | null> {
    const mgr = getUserManager();
    const user = await mgr.getUser();

    if (!user || user.expired) {
      // Attempt silent renewal — Keycloak may have an active SSO session
      try {
        const renewed = await mgr.signinSilent();
        return renewed?.access_token ?? null;
      } catch {
        // Silent renew failed — redirect to login
        await mgr.signinRedirect();
        return null;
      }
    }

    return user.access_token;
  }
}
